import React, { useState } from 'react';
import { CATALOGUES } from '../mock/mock';
import { FileText, ArrowRight, X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const CataloguesSection = () => {
  const [activeCatalog, setActiveCatalog] = useState(null);
  const [activeImgIndex, setActiveImgIndex] = useState(0);

  const openCatalog = (catalog) => {
    if (catalog.images && catalog.images.length > 0) {
      setActiveCatalog(catalog);
      setActiveImgIndex(0);
    } else {
      // Fallback directly to PDF if no images are configured
      window.open(catalog.pdf, '_blank', 'noopener,noreferrer');
    }
  };

  const closeCatalog = () => {
    setActiveCatalog(null);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (!activeCatalog) return;
    setActiveImgIndex((prev) => (prev + 1) % activeCatalog.images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (!activeCatalog) return;
    setActiveImgIndex((prev) => (prev - 1 + activeCatalog.images.length) % activeCatalog.images.length);
  };

  return (
    <section className="py-16 md:py-24 bg-[#FAF6F1]">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-3">Our Collections</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#2C2416] leading-tight">
            Curated Gifting Catalogues
          </h2>
          <p className="text-[#5B4A3A] mt-4 text-base md:text-lg leading-relaxed">
            Browse through our collection-specific catalogues. Click explore more to preview items in our interactive gallery or download the full PDF catalogue.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {CATALOGUES.map((catalog) => (
            <div 
              key={catalog.id} 
              className="group relative rounded-2xl overflow-hidden bg-[#2C2416] aspect-[4/3] shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-end"
            >
              {/* Background Image with Zoom Effect */}
              <div className="absolute inset-0 z-0">
                <img 
                  src={catalog.img} 
                  alt={catalog.title} 
                  className="w-full h-full object-cover opacity-85 group-hover:scale-105 transition-transform duration-700" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2416]/95 via-[#2C2416]/50 to-transparent" />
              </div>

              {/* Content Panel */}
              <div className="relative z-10 p-6 md:p-8 flex flex-col justify-end h-full">
                <div className="mb-2 w-10 h-10 rounded-lg bg-[#FAF6F1]/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-[#F4D5C5] group-hover:text-white transition-colors duration-300">
                  <FileText className="w-5 h-5" />
                </div>
                
                <h3 className="font-serif-display text-white text-2xl md:text-3xl leading-tight">
                  {catalog.title}
                </h3>
                
                <p className="text-white/70 text-sm mt-2 leading-relaxed">
                  {catalog.tagline}
                </p>

                <div className="mt-5 flex gap-2">
                  <button 
                    onClick={() => openCatalog(catalog)}
                    className="inline-flex items-center gap-2 bg-[#F4D5C5] hover:bg-white text-[#2C2416] hover:text-[#2C2416] font-medium text-xs uppercase tracking-wider px-5 py-2.5 rounded-full transition-all duration-300 shadow-sm cursor-pointer"
                  >
                    Explore More
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Interactive Gallery Lightbox Modal */}
      {activeCatalog && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={closeCatalog}
        >
          <div 
            className="relative bg-[#FAF6F1] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 md:p-6 border-b border-[#EFE3D2] flex justify-between items-center bg-white">
              <div>
                <h3 className="font-serif-display text-2xl text-[#2C2416] leading-none">
                  {activeCatalog.title}
                </h3>
                <p className="text-xs text-[#7A5C4C] mt-1.5">{activeCatalog.tagline}</p>
              </div>
              <button 
                onClick={closeCatalog}
                className="w-10 h-10 rounded-full flex items-center justify-center bg-[#FAF6F1] hover:bg-[#F4D5C5] text-[#2C2416] transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body / Image Carousel */}
            <div className="relative flex-1 bg-black min-h-[300px] md:min-h-[450px] flex items-center justify-center p-4">
              {activeCatalog.images && activeCatalog.images.length > 0 && (
                <>
                  <img 
                    src={activeCatalog.images[activeImgIndex]} 
                    alt={`${activeCatalog.title} Page ${activeImgIndex + 1}`} 
                    className="max-w-full max-h-[60vh] object-contain select-none"
                  />
                  
                  {activeCatalog.images.length > 1 && (
                    <>
                      {/* Left Navigation */}
                      <button 
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white flex items-center justify-center transition-colors"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>

                      {/* Right Navigation */}
                      <button 
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white flex items-center justify-center transition-colors"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>

                      {/* Pagination Indicator */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md px-4 py-1.5 rounded-full text-white text-xs uppercase tracking-widest">
                        {activeImgIndex + 1} / {activeCatalog.images.length}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 md:p-6 border-t border-[#EFE3D2] flex flex-col sm:flex-row justify-between items-center gap-4 bg-white">
              <span className="text-xs text-[#5B4A3A]">
                Showing sample catalogue pages. Update references in `mock.js` to change files.
              </span>
              <a 
                href={activeCatalog.pdf} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#2C2416] hover:bg-[#C58B76] text-white font-medium text-xs uppercase tracking-wider px-6 py-3 rounded-full transition-colors shadow-md"
              >
                <Download className="w-4 h-4" />
                Download Full PDF
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CataloguesSection;
